import { AnimatePresence, motion } from 'framer-motion';
import jsPDF from 'jspdf';
import { Calendar, Download, Loader2, MapPin, Search, Trash2 } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import userService from '../../services/userService';
import { getAllSites } from '../SiteManagement/siteApi';
import { fdocApi } from '../Tax/FdocApi';
import { vendorApi } from '../Vender/vendorApi';
import { Card } from '../ui';
import { purchaseOrderApi } from './PurchaseOrderApi';

const getCurrentMonth = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

/** FDOC API uses `latest_version_no`; some payloads use `version_no` / `versionNo`. */
const resolveFdocVersion = (...sources) => {
  for (const src of sources) {
    if (src == null) continue;
    const v = src.latest_version_no ?? src.version_no ?? src.versionNo;
    if (v != null && v !== '') return v;
  }
  return null;
};

/**
 * Transforms API purchase order data to component's expected format
 */
const transformPurchaseOrder = (apiPo) => {
  // Calculate totals from line items
  let subtotal = 0;
  let discount = 0;
  const items = (apiPo.lines || []).map((line) => {
    const quantity = line.quantity || line.quantity_ordered || 0;
    const unitCost = line.unit_cost || line.rate || 0;
    const taxRate = line.tax_rate || 0;
    const taxAmount = line.tax_amount || (quantity * unitCost * taxRate / 100);
    const lineTotal = (quantity * unitCost) + taxAmount;
    subtotal += lineTotal;
    
    return {
      name: line.description || line.item_name || 'Unknown Item',
      hsnCode: line.hsn_code || '',
      units: quantity,
      rate: unitCost,
      tax: taxRate,
      amount: lineTotal,
    };
  });

  // Calculate grand total
  // Check if API provides total_amount directly, otherwise calculate from line items
  const freightTotal = apiPo.freight_total || apiPo.freight_charges || 0;
  const apiTotal = apiPo.total_amount || apiPo.grand_total || apiPo.total || null;
  const grandTotal = apiTotal !== null ? apiTotal : (subtotal - discount + freightTotal);

  // Extract billing address (handle different possible structures)
  const billingAddress = apiPo.billing_address || {};
  
  return {
    id: apiPo.po_id || apiPo.id,
    poNumber: apiPo.po_number || apiPo.poNumber || 'N/A',
    vendor: apiPo.vendor_name || apiPo.vendor || 'Unknown Vendor',
    poDate: apiPo.po_date || apiPo.poDate || '',
    expectedDeliveryDate: apiPo.expected_delivery_date || apiPo.expectedDeliveryDate || '',
    paymentDate: apiPo.payment_date || apiPo.paymentDate || '',
    paymentMode: apiPo.payment_mode || apiPo.paymentMode || 'Not specified',
    siteId: apiPo.site_id || apiPo.siteId || '',
    siteName: apiPo.site_name || apiPo.siteName || '',
    billingAddress: {
      firstName: billingAddress.first_name || billingAddress.firstName || '',
      lastName: billingAddress.last_name || billingAddress.lastName || '',
      email: billingAddress.email || '',
      address: billingAddress.address || billingAddress.street || '',
      city: billingAddress.city || '',
      state: billingAddress.state || '',
      country: billingAddress.country || 'India',
      pinCode: billingAddress.pin_code || billingAddress.pinCode || billingAddress.zip || '',
      mobileNumber: billingAddress.mobile_number || billingAddress.mobileNumber || billingAddress.phone || '',
      panNumber: billingAddress.pan_number || billingAddress.panNumber || '',
      gstNumber: billingAddress.gst_number || billingAddress.gstNumber || '',
    },
    items,
    subtotal: subtotal,
    discount: discount,
    grandTotal: grandTotal,
    status: apiPo.status || 'Pending',
    // Preserve FDOC reference if present
    fdoc_id: apiPo.fdoc_id,
    fdoc_document: apiPo.fdoc_document,
    fileMissing: apiPo.fileMissing || false,
    version_no: resolveFdocVersion(apiPo, apiPo.fdoc_document),
    versionNo: resolveFdocVersion(apiPo, apiPo.fdoc_document),
  };
};

const resolveSiteValue = (site) => {
  return site?.site_id ?? site?.siteId ?? site?.id ?? site?._id ?? site?.site_code ?? site?.siteCode ?? '';
};

const PurchaseOrderView = ({ 
  externalSite = null, 
  externalMonth = null, 
  externalSites = null,
  hideSelectors = false,
  fetchFromFdoc = false,
  esignedOnly = false,
}) => {
  // Log screen view
  useScreenLogging('PurchaseOrderView', {
    component: 'PurchaseOrderView',
    screenType: 'purchase-orders',
  });

  const navigate = useNavigate();
  const location = useLocation();
  const [selectedSite, setSelectedSite] = useState(externalSite || null);
  const [selectedMonth, setSelectedMonth] = useState(externalMonth || getCurrentMonth());
  const [selectedCard, setSelectedCard] = useState(null);
  const [sites, setSites] = useState(externalSites || []);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSitesLoading, setIsSitesLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0); // Key to force refresh
  const [poNumberFilter, setPoNumberFilter] = useState('');
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [downloadUrlExpiresIn, setDownloadUrlExpiresIn] = useState(null);
  const [documentContentType, setDocumentContentType] = useState(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [cancellingFdocId, setCancellingFdocId] = useState(null);
  const autoCancelledFdocIdsRef = useRef(new Set());

  const autoCancelMissingFdoc = async (fdocId, reason = 'file missing, auto-cancelled from UI') => {
    if (!fdocId) return false;
    if (autoCancelledFdocIdsRef.current.has(fdocId)) return true;
    autoCancelledFdocIdsRef.current.add(fdocId);
    try {
      await fdocApi.cancel(fdocId, { reason });
      return true;
    } catch (e) {
      // If cancel fails, allow retry later (but don't block user with errors during background cleanup).
      autoCancelledFdocIdsRef.current.delete(fdocId);
      console.warn('[PurchaseOrderView] Auto-cancel failed for missing FDOC:', fdocId, e?.message || e);
      return false;
    }
  };

  // Handle location state from navigation (e.g., after creating a purchase order)
  useEffect(() => {
    if (location.state) {
      const { siteId, site, month, poId, poNumber, refresh, justCreated } = location.state;
      
      console.log('PurchaseOrderView: Received location state:', {
        siteId,
        month,
        poId,
        poNumber,
        refresh,
        justCreated
      });
      
      // Auto-select site if provided
      if (siteId && sites.length > 0) {
        const siteToSelect = sites.find(s => 
          s.site_id === siteId || s.id === siteId || s.siteCode === siteId
        );
        if (siteToSelect) {
          setSelectedSite(siteToSelect);
        }
      } else if (site) {
        setSelectedSite(site);
      }
      
      // Auto-select month if provided
      if (month) {
        setSelectedMonth(month);
      }
      
      // Trigger refresh if indicated
      if (refresh) {
        // If just created, add an additional delay to allow backend to commit
        if (justCreated) {
          console.log('PurchaseOrderView: PO just created, waiting before refresh to allow backend commit...');
          setTimeout(() => {
            setRefreshKey(prev => prev + 1);
          }, 1000); // Additional 1 second delay after navigation
        } else {
          setRefreshKey(prev => prev + 1);
        }
      }
      
      // Clear location state to prevent re-triggering on re-render
      window.history.replaceState({}, document.title);
    }
  }, [location.state, sites]);

  // Update internal state when external props change
  useEffect(() => {
    if (externalSite !== null) {
      setSelectedSite(externalSite);
    }
  }, [externalSite]);

  useEffect(() => {
    if (externalMonth !== null) {
      setSelectedMonth(externalMonth);
    }
  }, [externalMonth]);

  useEffect(() => {
    if (externalSites !== null) {
      setSites(externalSites);
    }
  }, [externalSites]);

  // Fetch sites only if not provided externally
  useEffect(() => {
    if (externalSites !== null) {
      // Sites are provided externally, don't fetch
      return;
    }

    const fetchSites = async () => {
      setIsSitesLoading(true);
      try {
        const response = await getAllSites();
        const sitesList = Array.isArray(response?.sites)
          ? response.sites
          : Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
          ? response.data
          : [];
        
        const sorted = [...sitesList].sort((a, b) =>
          (a?.site_name || a?.name || '').localeCompare(b?.site_name || b?.name || '')
        );
        
        setSites(sorted.length > 0 ? sorted : []);
      } catch (error) {
        console.error('Error fetching sites:', error);
        setSites([]);
      } finally {
        setIsSitesLoading(false);
      }
    };

    fetchSites();
  }, [externalSites]);

  // Fetch purchase orders when site or month changes
  useEffect(() => {
    const fetchPurchaseOrders = async () => {
      if (!selectedSite) {
        setPurchaseOrders([]);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // Calculate start and end dates for the selected month
        const [year, month] = selectedMonth.split('-');
        const startDate = `${year}-${month}-01`;
        const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate();
        const endDate = `${year}-${month}-${String(lastDay).padStart(2, '0')}`;

        // Get site_id for API filtering (may be null when fetchFromFdoc is true)
        const siteId = selectedSite ? resolveSiteValue(selectedSite) : null;

        // If fetchFromFdoc is true, fetch from FDOC API instead of purchase order API
        if (fetchFromFdoc) {
          try {
            console.log('PurchaseOrderView: Fetching purchase orders from FDOC (Purchase Order OO)');
            const fdocParams = { limit: 500 };
            if (esignedOnly) {
              fdocParams.esign_status = 'applied';
            }
            const fdocResponse = await fdocApi.listDocuments(fdocParams);
            
            // Process FDOC documents to find Purchase Order types
            const fdocBatchSize = 10;
            const fdocPOs = [];
            
            for (let i = 0; i < fdocResponse.items.length; i += fdocBatchSize) {
              const batch = fdocResponse.items.slice(i, i + fdocBatchSize);
              const batchResults = await Promise.allSettled(
              batch.map(async (doc) => {
                  try {
                    const fdocId = doc?.fdoc_id || doc?.id;
                    if (!fdocId) {
                      console.warn('PurchaseOrderView: FDOC item missing fdoc_id/id, skipping:', doc);
                      return null;
                    }

                    const fullDoc = await fdocApi.getDocument(fdocId);
                    const extractedData = fullDoc.extracted_data;
                    let docType = extractedData?.type || 'UNKNOWN';
                    
                    // Infer type from file name if needed
                    if (docType === 'UNKNOWN' || !docType) {
                      const fileName = (fullDoc?.file_name || doc?.file_name || '').toLowerCase();
                      const fileKey = (fullDoc?.s3_key || doc?.s3_key || '').toLowerCase();
                      const combinedText = `${fileName} ${fileKey}`;
                      
                      if (combinedText.includes('po') || combinedText.includes('purchase') || combinedText.includes('purchase order')) {
                        docType = 'Purchase Order';
                      }
                    }
                    
                    // Normalize PO types
                    const normalizedType = docType.toLowerCase();
                    if (normalizedType === 'po' || normalizedType === 'purchase order' || normalizedType === 'purchase orders') {
                      // Get site_id from document
                      const docSiteId = fullDoc?.site_id || doc.site_id || fullDoc?.siteId || doc.siteId || null;
                      
                      // Check date range if provided
                      const docDate = doc.uploaded_at ? new Date(doc.uploaded_at).toISOString().split('T')[0] : null;
                      if (docDate) {
                        if (startDate && docDate < startDate) {
                          return null;
                        }
                        if (endDate && docDate > endDate) {
                          return null;
                        }
                      }
                      
                      // Transform FDOC document to purchase order format
                      const extractedFields = extractedData?.fields || {};
                      const textBlocks = extractedFields.text_blocks || [];
                      const rawText = extractedFields.raw_text || '';
                      
                      // Debug: Log available fields to understand structure
                      // Use console.table for better readability
                      console.group(`[PurchaseOrderView] FDOC Document ${fdocId}`);
                      console.log('File name:', fullDoc?.file_name || doc?.file_name);
                      console.log('Document type:', extractedData?.type);
                      console.log('Date:', extractedData?.date);
                      console.log('Field keys:', Object.keys(extractedFields));
                      console.log('Text blocks:', textBlocks);
                      console.table(extractedFields); // This will show all fields in a table format
                      console.log('Full extracted_data:', extractedData);
                      console.groupEnd();
                      
                      // Extract PO number from text_blocks or structured fields
                      let poNumber = extractedFields.po_number || extractedFields.poNumber || extractedFields.purchase_order_number;
                      if (!poNumber && textBlocks.length > 0) {
                        // Look for PO number pattern in text_blocks (e.g., "PO-769384")
                        const poMatch = textBlocks.find(block => 
                          typeof block === 'string' && /PO[- ]?\d+/i.test(block)
                        );
                        if (poMatch) {
                          poNumber = poMatch.match(/PO[- ]?(\d+)/i)?.[0] || poMatch;
                        }
                      }
                      if (!poNumber) {
                        poNumber = `FDOC-${doc.fdoc_id.substring(0, 8).toUpperCase()}`;
                      }
                      
                      // Extract PO date from text_blocks or structured fields
                      let poDate = extractedFields.po_date || extractedFields.date;
                      if (!poDate && textBlocks.length > 0) {
                        // Look for date pattern in text_blocks (e.g., "PO Date: 15 Dec 2025")
                        const dateMatch = textBlocks.find(block => 
                          typeof block === 'string' && /po\s+date.*\d{1,2}\s+\w+\s+\d{4}/i.test(block)
                        );
                        if (dateMatch) {
                          const dateMatchResult = dateMatch.match(/(\d{1,2}\s+\w+\s+\d{4})/i);
                          if (dateMatchResult) {
                            try {
                              poDate = new Date(dateMatchResult[1]).toISOString().split('T')[0];
                            } catch (e) {
                              // If date parsing fails, use docDate
                            }
                          }
                        }
                      }
                      if (!poDate) {
                        poDate = docDate || new Date().toISOString().split('T')[0];
                      }
                      
                      // Extract expected delivery date from text_blocks
                      let expectedDeliveryDate = extractedFields.expected_delivery_date || extractedFields.delivery_date || '';
                      if (!expectedDeliveryDate && textBlocks.length > 0) {
                        const deliveryIndex = textBlocks.findIndex(block => 
                          typeof block === 'string' && /expected\s+delivery/i.test(block)
                        );
                        if (deliveryIndex >= 0) {
                          // Check next 1-3 positions for the date
                          for (let offset = 1; offset <= 3 && deliveryIndex + offset < textBlocks.length; offset++) {
                            const deliveryValue = textBlocks[deliveryIndex + offset];
                            if (typeof deliveryValue === 'string') {
                              const dateMatch = deliveryValue.match(/(\d{1,2}\s+\w+\s+\d{4})/i);
                              if (dateMatch) {
                                try {
                                  expectedDeliveryDate = new Date(dateMatch[1]).toISOString().split('T')[0];
                                  console.log(`[PurchaseOrderView] Extracted expected delivery date: ${expectedDeliveryDate}`);
                                  break;
                                } catch (e) {
                                  // Continue searching
                                }
                              }
                            }
                          }
                        }
                      }
                      
                      // Extract payment mode from text_blocks
                      let paymentMode = extractedFields.payment_mode || 'Not specified';
                      if (paymentMode === 'Not specified' && textBlocks.length > 0) {
                        const paymentIndex = textBlocks.findIndex(block => 
                          typeof block === 'string' && /payment\s+mode/i.test(block)
                        );
                        if (paymentIndex >= 0 && paymentIndex + 1 < textBlocks.length) {
                          const paymentValue = textBlocks[paymentIndex + 1];
                          if (typeof paymentValue === 'string' && paymentValue.trim().length > 0) {
                            // Skip if it looks like a date or UUID
                            if (!/^\d{1,2}\s+\w+\s+\d{4}$/i.test(paymentValue) && 
                                !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(paymentValue)) {
                              paymentMode = paymentValue.trim();
                              console.log(`[PurchaseOrderView] Extracted payment mode: ${paymentMode}`);
                            }
                          }
                        }
                        // Also check for "PAYMENT" label followed by mode
                        if (paymentMode === 'Not specified') {
                          const paymentLabelIndex = textBlocks.findIndex(block => 
                            typeof block === 'string' && block.toUpperCase() === 'PAYMENT'
                          );
                          if (paymentLabelIndex >= 0 && paymentLabelIndex + 1 < textBlocks.length) {
                            const paymentValue = textBlocks[paymentLabelIndex + 1];
                            if (typeof paymentValue === 'string' && paymentValue.trim().length > 0) {
                              if (!/^\d{1,2}\s+\w+\s+\d{4}$/i.test(paymentValue) && 
                                  !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(paymentValue)) {
                                paymentMode = paymentValue.trim();
                                console.log(`[PurchaseOrderView] Extracted payment mode from PAYMENT label: ${paymentMode}`);
                              }
                            }
                          }
                        }
                      }
                      
                      // Extract payment date from text_blocks
                      let paymentDate = extractedFields.payment_date || '';
                      if (!paymentDate && textBlocks.length > 0) {
                        const paymentDateIndex = textBlocks.findIndex(block => 
                          typeof block === 'string' && /payment\s+date/i.test(block)
                        );
                        if (paymentDateIndex >= 0) {
                          for (let offset = 1; offset <= 3 && paymentDateIndex + offset < textBlocks.length; offset++) {
                            const dateValue = textBlocks[paymentDateIndex + offset];
                            if (typeof dateValue === 'string') {
                              const dateMatch = dateValue.match(/(\d{1,2}\s+\w+\s+\d{4})/i);
                              if (dateMatch) {
                                try {
                                  paymentDate = new Date(dateMatch[1]).toISOString().split('T')[0];
                                  console.log(`[PurchaseOrderView] Extracted payment date: ${paymentDate}`);
                                  break;
                                } catch (e) {
                                  // Continue searching
                                }
                              }
                            }
                          }
                        }
                      }
                      
                      // Look up site name from sites list if available
                      let siteName = 'N/A';
                      const sitesList = externalSites && externalSites.length > 0 ? externalSites : sites;
                      if (docSiteId && sitesList.length > 0) {
                        const site = sitesList.find(s => resolveSiteValue(s) === docSiteId);
                        if (site) {
                          siteName = site.site_name || site.name || 'N/A';
                        }
                      }
                      // Fallback to fullDoc site_name if sites list lookup fails
                      if (siteName === 'N/A' && fullDoc?.site_name) {
                        siteName = fullDoc.site_name;
                      }
                      
                      // Extract vendor name - first try to get vendor ID from text_blocks and look it up
                      let vendorName = null;
                      let vendorId = null;
                      
                      // Check if vendor ID is in text_blocks (appears after 'VENDOR' label)
                      if (textBlocks.length > 0) {
                        const vendorIndex = textBlocks.findIndex(block => 
                          typeof block === 'string' && block.toUpperCase() === 'VENDOR'
                        );
                        if (vendorIndex >= 0 && vendorIndex + 1 < textBlocks.length) {
                          const potentialVendorId = textBlocks[vendorIndex + 1];
                          // Check if it looks like a UUID
                          if (typeof potentialVendorId === 'string' && 
                              /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(potentialVendorId)) {
                            vendorId = potentialVendorId;
                            console.log(`[PurchaseOrderView] Found vendor ID in text_blocks: ${vendorId}`);
                          }
                        }
                      }
                      
                      // If we found a vendor ID, try to look up the vendor name
                      if (vendorId) {
                        try {
                          const vendorResult = await vendorApi.getVendor(vendorId);
                          if (vendorResult.success && vendorResult.vendor) {
                            vendorName = vendorResult.vendor.vendor_name || vendorResult.vendor.vendorName;
                            console.log(`[PurchaseOrderView] Looked up vendor name from ID: ${vendorName}`);
                          }
                        } catch (err) {
                          console.warn(`[PurchaseOrderView] Failed to look up vendor ${vendorId}:`, err.message);
                        }
                      }
                      
                      // If vendor name not found from ID lookup, check extracted fields with common variations
                      if (!vendorName) {
                        vendorName = extractedFields.vendor_name || 
                                    extractedFields.vendorName || 
                                    extractedFields.vendor || 
                                    extractedFields.supplier_name || 
                                    extractedFields.supplierName ||
                                    extractedFields.supplier ||
                                    extractedFields.company_name ||
                                    extractedFields.companyName ||
                                    extractedFields.from ||
                                    extractedFields.from_name ||
                                    extractedFields.fromName ||
                                    extractedFields.bill_to_name ||
                                    extractedFields.billToName ||
                                    extractedFields.bill_to ||
                                    extractedFields.billTo ||
                                    extractedFields.seller_name ||
                                    extractedFields.sellerName ||
                                    extractedFields.seller ||
                                    extractedFields.party_name ||
                                    extractedFields.partyName ||
                                    extractedFields.to_name ||
                                    extractedFields.toName;
                      }
                      
                      // If still not found, try to extract from raw_text
                      if (!vendorName && rawText) {
                        // Look for vendor-related patterns in raw text
                        const vendorPatterns = [
                          /vendor[:\s]+([^\n]+)/i,
                          /supplier[:\s]+([^\n]+)/i,
                          /from[:\s]+([^\n]+)/i,
                        ];
                        for (const pattern of vendorPatterns) {
                          const match = rawText.match(pattern);
                          if (match && match[1]) {
                            const potentialName = match[1].trim();
                            // Skip if it looks like a UUID or date
                            if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(potentialName) &&
                                !/^\d{1,2}\s+\w+\s+\d{4}$/i.test(potentialName)) {
                              vendorName = potentialName;
                              break;
                            }
                          }
                        }
                      }
                      
                      // Check billing_address for vendor name
                      if (!vendorName && extractedFields.billing_address) {
                        const billingAddr = extractedFields.billing_address;
                        vendorName = billingAddr.name || 
                                    billingAddr.company_name ||
                                    billingAddr.companyName ||
                                    billingAddr.vendor_name ||
                                    billingAddr.vendorName ||
                                    billingAddr.vendor ||
                                    billingAddr.supplier_name ||
                                    billingAddr.supplierName;
                      }
                      
                      // Check shipping_address for vendor name
                      if (!vendorName && extractedFields.shipping_address) {
                        const shippingAddr = extractedFields.shipping_address;
                        vendorName = shippingAddr.name || 
                                    shippingAddr.company_name ||
                                    shippingAddr.companyName ||
                                    shippingAddr.vendor_name ||
                                    shippingAddr.vendorName;
                      }
                      
                      // If still not found, try to find any field that might contain vendor info
                      // by checking all field values for common vendor-related keywords
                      if (!vendorName || vendorName === 'Unknown Vendor') {
                        const allFieldKeys = Object.keys(extractedFields);
                        const vendorKeywords = ['vendor', 'supplier', 'company', 'seller', 'party'];
                        
                        for (const key of allFieldKeys) {
                          const value = extractedFields[key];
                          if (typeof value === 'string' && value.trim().length > 0) {
                            const lowerKey = key.toLowerCase();
                            const lowerValue = value.toLowerCase();
                            
                            // Check if field name or value contains vendor-related keywords
                            if (vendorKeywords.some(keyword => lowerKey.includes(keyword) || lowerValue.includes(keyword))) {
                              // Skip if it looks like an address, date, or number
                              if (!lowerValue.match(/^\d+$/) && 
                                  !lowerValue.match(/^\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}$/) &&
                                  !lowerValue.includes('@') &&
                                  !lowerValue.match(/^\d{6,}$/)) {
                                vendorName = value.trim();
                                break;
                              }
                            }
                          }
                        }
                      }
                      
                      // Check if vendor info is in fullDoc metadata or other locations
                      if (!vendorName || vendorName === 'Unknown Vendor') {
                        vendorName = fullDoc?.vendor_name || 
                                    fullDoc?.vendorName || 
                                    fullDoc?.vendor ||
                                    fullDoc?.supplier_name ||
                                    fullDoc?.supplierName;
                      }
                      
                      // Final fallback - use file name if it might contain vendor info
                      if (!vendorName || vendorName === 'Unknown Vendor') {
                        const fileName = fullDoc?.file_name || doc?.file_name || '';
                        // Only use file name if it doesn't look like a generic name
                        if (fileName && !fileName.toLowerCase().includes('invoice') && 
                            !fileName.toLowerCase().includes('po') &&
                            !fileName.toLowerCase().includes('purchase')) {
                          const nameWithoutExt = fileName.replace(/\.[^/.]+$/, '');
                          if (nameWithoutExt.length > 2 && nameWithoutExt.length < 50) {
                            vendorName = nameWithoutExt;
                          }
                        }
                      }
                      
                      // Final fallback
                      if (!vendorName || vendorName === 'Unknown Vendor') {
                        vendorName = 'Unknown Vendor';
                      }
                      
                      // If vendor is still unknown, log detailed info for debugging
                      if (!vendorName || vendorName === 'Unknown Vendor') {
                        console.warn(`[PurchaseOrderView] ⚠️ VENDOR NOT FOUND for ${doc.fdoc_id}`);
                        console.warn(`[PurchaseOrderView] Available field keys:`, Object.keys(extractedFields));
                        console.warn(`[PurchaseOrderView] Field values:`, extractedFields);
                        console.warn(`[PurchaseOrderView] Full extracted_data:`, extractedData);
                        console.warn(`[PurchaseOrderView] File name:`, fullDoc?.file_name || doc?.file_name);
                      }
                      
                      console.log(`[PurchaseOrderView] Extracted vendor name for ${doc.fdoc_id}:`, vendorName);
                      console.log(`[PurchaseOrderView] All field keys for ${doc.fdoc_id}:`, Object.keys(extractedFields));
                      
                      // Extract amounts from text_blocks or structured fields
                      let subtotal = 0;
                      let discountAmount = 0;
                      let freightTotal = 0;
                      let totalAmount = 0;
                      
                      // Try to extract from structured fields first
                      subtotal = parseFloat(extractedFields.subtotal || extractedFields.sub_total || 0);
                      discountAmount = parseFloat(extractedFields.discount || extractedFields.discount_amount || 0);
                      freightTotal = parseFloat(extractedFields.freight || extractedFields.freight_total || 0);
                      totalAmount = parseFloat(extractedFields.grand_total || extractedFields.total_amount || extractedFields.total || 0);
                      
                      // If amounts are 0, try to extract from text_blocks
                      if (textBlocks.length > 0 && (totalAmount === 0 || subtotal === 0)) {
                        // Extract grand total from text_blocks
                        const grandTotalIndex = textBlocks.findIndex(block => 
                          typeof block === 'string' && /grand\s+total/i.test(block)
                        );
                        if (grandTotalIndex >= 0) {
                          // Check next 1-3 positions for the value
                          for (let offset = 1; offset <= 3 && grandTotalIndex + offset < textBlocks.length; offset++) {
                            const totalValue = textBlocks[grandTotalIndex + offset];
                            if (typeof totalValue === 'string') {
                              // Remove currency symbols, commas, and whitespace, then parse
                              const cleanedTotal = totalValue.replace(/[₹,\s]/g, '');
                              const parsedTotal = parseFloat(cleanedTotal);
                              if (!isNaN(parsedTotal) && parsedTotal > 0) {
                                totalAmount = parsedTotal;
                                console.log(`[PurchaseOrderView] Extracted grand total from text_blocks at offset ${offset}: ${totalAmount}`);
                                break;
                              }
                            }
                          }
                        }
                        
                        // Extract subtotal from text_blocks
                        const subtotalIndex = textBlocks.findIndex(block => 
                          typeof block === 'string' && /sub\s*total/i.test(block)
                        );
                        if (subtotalIndex >= 0 && subtotalIndex + 1 < textBlocks.length && subtotal === 0) {
                          const subtotalValue = textBlocks[subtotalIndex + 1];
                          if (typeof subtotalValue === 'string') {
                            const cleanedSubtotal = subtotalValue.replace(/[₹,\s]/g, '');
                            const parsedSubtotal = parseFloat(cleanedSubtotal);
                            if (!isNaN(parsedSubtotal) && parsedSubtotal > 0) {
                              subtotal = parsedSubtotal;
                              console.log(`[PurchaseOrderView] Extracted subtotal from text_blocks: ${subtotal}`);
                            }
                          }
                        }
                        
                        // Extract discount from text_blocks
                        const discountIndex = textBlocks.findIndex(block => 
                          typeof block === 'string' && /discount/i.test(block)
                        );
                        if (discountIndex >= 0 && discountIndex + 1 < textBlocks.length && discountAmount === 0) {
                          const discountValue = textBlocks[discountIndex + 1];
                          if (typeof discountValue === 'string') {
                            const cleanedDiscount = discountValue.replace(/[₹,\s]/g, '');
                            const parsedDiscount = parseFloat(cleanedDiscount);
                            if (!isNaN(parsedDiscount)) {
                              discountAmount = parsedDiscount;
                              console.log(`[PurchaseOrderView] Extracted discount from text_blocks: ${discountAmount}`);
                            }
                          }
                        }
                        
                        // Extract freight from text_blocks
                        const freightIndex = textBlocks.findIndex(block => 
                          typeof block === 'string' && /freight|shipping|delivery/i.test(block)
                        );
                        if (freightIndex >= 0 && freightIndex + 1 < textBlocks.length && freightTotal === 0) {
                          const freightValue = textBlocks[freightIndex + 1];
                          if (typeof freightValue === 'string') {
                            const cleanedFreight = freightValue.replace(/[₹,\s]/g, '');
                            const parsedFreight = parseFloat(cleanedFreight);
                            if (!isNaN(parsedFreight) && parsedFreight > 0) {
                              freightTotal = parsedFreight;
                              console.log(`[PurchaseOrderView] Extracted freight from text_blocks: ${freightTotal}`);
                            }
                          }
                        }
                      }
                      
                      // If grand total is still 0, try to extract from raw_text
                      if (totalAmount === 0 && rawText) {
                        const grandTotalMatch = rawText.match(/grand\s+total[:\s]*₹?\s*([\d,]+\.?\d*)/i);
                        if (grandTotalMatch && grandTotalMatch[1]) {
                          const cleanedTotal = grandTotalMatch[1].replace(/,/g, '');
                          const parsedTotal = parseFloat(cleanedTotal);
                          if (!isNaN(parsedTotal) && parsedTotal > 0) {
                            totalAmount = parsedTotal;
                            console.log(`[PurchaseOrderView] Extracted grand total from raw_text: ${totalAmount}`);
                          }
                        }
                      }
                      
                      // Extract billing address fields (mobile, PAN, GST) from text_blocks or structured fields
                      let billingAddress = extractedFields.billing_address || {};
                      let mobileNumber = billingAddress.mobile_number || billingAddress.mobileNumber || billingAddress.phone || '';
                      let panNumber = billingAddress.pan_number || billingAddress.panNumber || '';
                      let gstNumber = billingAddress.gst_number || billingAddress.gstNumber || '';
                      
                      // Try to extract from text_blocks if not found
                      if (textBlocks.length > 0) {
                        // Extract mobile number
                        if (!mobileNumber) {
                          const mobileIndex = textBlocks.findIndex(block => 
                            typeof block === 'string' && /mobile|phone|contact/i.test(block)
                          );
                          if (mobileIndex >= 0 && mobileIndex + 1 < textBlocks.length) {
                            const mobileValue = textBlocks[mobileIndex + 1];
                            if (typeof mobileValue === 'string' && /^\d{10}$/.test(mobileValue.replace(/[\s-]/g, ''))) {
                              mobileNumber = mobileValue.trim();
                              console.log(`[PurchaseOrderView] Extracted mobile number: ${mobileNumber}`);
                            }
                          }
                        }
                        
                        // Extract PAN number
                        if (!panNumber) {
                          const panIndex = textBlocks.findIndex(block => 
                            typeof block === 'string' && /pan/i.test(block)
                          );
                          if (panIndex >= 0 && panIndex + 1 < textBlocks.length) {
                            const panValue = textBlocks[panIndex + 1];
                            if (typeof panValue === 'string' && /^[A-Z]{5}\d{4}[A-Z]$/i.test(panValue.trim())) {
                              panNumber = panValue.trim().toUpperCase();
                              console.log(`[PurchaseOrderView] Extracted PAN number: ${panNumber}`);
                            }
                          }
                        }
                        
                        // Extract GST number
                        if (!gstNumber) {
                          const gstIndex = textBlocks.findIndex(block => 
                            typeof block === 'string' && /gst/i.test(block)
                          );
                          if (gstIndex >= 0 && gstIndex + 1 < textBlocks.length) {
                            const gstValue = textBlocks[gstIndex + 1];
                            if (typeof gstValue === 'string' && /^\d{2}[A-Z]{5}\d{4}[A-Z]\d[Z][A-Z\d]$/i.test(gstValue.trim())) {
                              gstNumber = gstValue.trim().toUpperCase();
                              console.log(`[PurchaseOrderView] Extracted GST number: ${gstNumber}`);
                            }
                          }
                        }
                      }
                      
                      // Update billing address with extracted values
                      billingAddress = {
                        ...billingAddress,
                        mobile_number: mobileNumber || billingAddress.mobile_number || billingAddress.mobileNumber || billingAddress.phone || '',
                        pan_number: panNumber || billingAddress.pan_number || billingAddress.panNumber || '',
                        gst_number: gstNumber || billingAddress.gst_number || billingAddress.gstNumber || '',
                      };
                      
                      // Extract line items from text_blocks or structured fields
                      let lineItems = extractedFields.line_items || extractedFields.items || [];
                      
                      console.log(`[PurchaseOrderView] Initial lineItems from extractedFields:`, lineItems.length, lineItems);
                      console.log(`[PurchaseOrderView] Text blocks for item extraction:`, textBlocks);
                      
                      // If no line items found, try to parse from text_blocks or raw_text
                      if (lineItems.length === 0) {
                        console.log(`[PurchaseOrderView] No line items in extractedFields, trying to extract from text_blocks/raw_text`);
                        // First, try to find items in text_blocks by looking for table headers
                        if (textBlocks.length > 0) {
                          console.log(`[PurchaseOrderView] Searching ${textBlocks.length} text blocks for items`);
                          
                          // Look for table header row (S.NO, NAME, HSN CODE, UNITS, RATE, TAX, AMOUNT)
                          // Headers are in separate blocks, so we need to check for a sequence
                          let headerStartIndex = -1;
                          
                          console.log(`[PurchaseOrderView] Searching for table header in ${textBlocks.length} text blocks`);
                          
                          // Check for header sequence (usually 7 consecutive blocks: S.NO, NAME, HSN CODE, UNITS, RATE, TAX (%), AMOUNT)
                          for (let i = 0; i < textBlocks.length - 6; i++) {
                            const blocks = textBlocks.slice(i, i + 7);
                            const blockStrings = blocks.filter(b => typeof b === 'string').map(b => b.toUpperCase());
                            
                            // Check if we have a sequence matching the header pattern
                            let matches = 0;
                            const checks = [];
                            
                            if (blockStrings[0] && (blockStrings[0].includes('S.NO') || blockStrings[0].includes('S NO') || blockStrings[0].includes('SNO'))) {
                              matches++;
                              checks.push('S.NO');
                            }
                            if (blockStrings[1] && blockStrings[1].includes('NAME')) {
                              matches++;
                              checks.push('NAME');
                            }
                            if (blockStrings[2] && (blockStrings[2].includes('HSN') || blockStrings[2].includes('CODE'))) {
                              matches++;
                              checks.push('HSN');
                            }
                            if (blockStrings[3] && blockStrings[3].includes('UNITS')) {
                              matches++;
                              checks.push('UNITS');
                            }
                            if (blockStrings[4] && blockStrings[4].includes('RATE')) {
                              matches++;
                              checks.push('RATE');
                            }
                            if (blockStrings[5] && blockStrings[5].includes('TAX')) {
                              matches++;
                              checks.push('TAX');
                            }
                            if (blockStrings[6] && blockStrings[6].includes('AMOUNT')) {
                              matches++;
                              checks.push('AMOUNT');
                            }
                            
                            if (matches >= 5) { // At least 5 out of 7 headers match
                              headerStartIndex = i;
                              console.log(`[PurchaseOrderView] ✅ Found table header sequence starting at index ${i} (${matches}/7 matches: ${checks.join(', ')}):`, blocks);
                              break;
                            }
                            
                            // Log first few attempts for debugging
                            if (i < 5) {
                              console.log(`[PurchaseOrderView] Checking index ${i}: blocks=${blocks.map(b => typeof b === 'string' ? b : 'N/A').join(', ')}, matches=${matches}`);
                            }
                          }
                          
                          if (headerStartIndex === -1) {
                            console.warn(`[PurchaseOrderView] ⚠️ No table header sequence found in text_blocks`);
                          }
                          
                          // If we found the header, extract items from the rows below
                          if (headerStartIndex >= 0) {
                            // Find where items start (usually right after the 7 header blocks)
                            let itemStartIndex = headerStartIndex + 7;
                            
                            // Look for the first row that starts with a number (S.NO)
                            for (let i = headerStartIndex + 7; i < textBlocks.length; i++) {
                              const block = textBlocks[i];
                              if (typeof block === 'string' && /^\d+$/.test(block.trim())) {
                                itemStartIndex = i;
                                console.log(`[PurchaseOrderView] Found first item row at index ${i}`);
                                break;
                              }
                            }
                            
                            // Extract items row by row
                            // Each row has: S.NO, NAME, HSN CODE, UNITS, RATE, TAX (%), AMOUNT
                            let currentIndex = itemStartIndex;
                            
                            while (currentIndex < textBlocks.length) {
                              // Check if we've reached the summary section (Subtotal, Discount, etc.)
                              const currentBlock = textBlocks[currentIndex];
                              if (typeof currentBlock === 'string') {
                                const upperBlock = currentBlock.toUpperCase();
                                if (upperBlock.includes('SUBTOTAL') || 
                                    upperBlock.includes('DISCOUNT') || 
                                    upperBlock.includes('GRAND TOTAL') ||
                                    upperBlock.includes('PAGE')) {
                                  console.log(`[PurchaseOrderView] Reached summary section at index ${currentIndex}, stopping item extraction`);
                                  break;
                                }
                              }
                              
                              // Try to extract one item row (7 fields: S.NO, NAME, HSN, UNITS, RATE, TAX, AMOUNT)
                              if (currentIndex + 6 < textBlocks.length) {
                                const sno = textBlocks[currentIndex];
                                const name = textBlocks[currentIndex + 1];
                                const hsn = textBlocks[currentIndex + 2];
                                const units = textBlocks[currentIndex + 3];
                                const rate = textBlocks[currentIndex + 4];
                                const tax = textBlocks[currentIndex + 5];
                                const amount = textBlocks[currentIndex + 6];
                                
                                // Validate that this looks like an item row
                                if (typeof sno === 'string' && /^\d+$/.test(sno.trim()) &&
                                    typeof name === 'string' && name.trim().length > 0 &&
                                    typeof units === 'string' && /^\d+(?:\.\d+)?$/.test(units.trim())) {
                                  
                                  const quantity = parseFloat(units) || 0;
                                  const unitPrice = parseFloat(rate) || 0;
                                  // Extract amount (remove currency symbols)
                                  const amountStr = typeof amount === 'string' ? amount.replace(/[₹,\s]/g, '') : '0';
                                  const lineTotal = parseFloat(amountStr) || 0;
                                  
                                  // Extract tax rate from tax field (e.g., "5%" -> 5)
                                  let taxRate = 0;
                                  if (typeof tax === 'string') {
                                    const taxMatch = tax.match(/(\d+(?:\.\d+)?)/);
                                    if (taxMatch) {
                                      taxRate = parseFloat(taxMatch[1]) || 0;
                                    }
                                  }
                                  
                                  // Calculate tax amount
                                  const baseTotal = quantity * unitPrice;
                                  const taxAmount = lineTotal - baseTotal;
                                  
                                  const item = {
                                    description: name.trim(),
                                    item_name: name.trim(),
                                    quantity: quantity,
                                    quantity_ordered: quantity,
                                    unit_cost: unitPrice,
                                    rate: unitPrice,
                                    unit_price: unitPrice,
                                    line_total: lineTotal,
                                    tax_rate: taxRate,
                                    tax_amount: taxAmount,
                                    hsn_code: typeof hsn === 'string' ? hsn.trim() : '',
                                  };
                                  
                                  lineItems.push(item);
                                  console.log(`[PurchaseOrderView] Extracted item from row starting at index ${currentIndex}:`, item);
                                  
                                  // Move to next row (skip 7 blocks)
                                  currentIndex += 7;
                                } else {
                                  // Doesn't look like an item row, move forward
                                  currentIndex++;
                                }
                              } else {
                                // Not enough blocks left, stop
                                break;
                              }
                            }
                            
                            if (lineItems.length > 0) {
                              console.log(`[PurchaseOrderView] Successfully extracted ${lineItems.length} line items from table format`);
                            }
                          } else {
                            console.log(`[PurchaseOrderView] No table header found, trying alternative extraction methods`);
                            // Fallback to old method
                            const itemIndices = [];
                            // Look for patterns that might indicate items (e.g., numbers followed by text, or item names)
                            for (let i = 0; i < textBlocks.length; i++) {
                              const block = textBlocks[i];
                              if (typeof block === 'string') {
                                // Check if this looks like an item entry (has quantity, name, price patterns)
                                // More flexible pattern: number, text (with spaces), number, number
                                const itemPattern = /^(\d+(?:\.\d+)?)\s+(.+?)\s+(\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)$/;
                                const match = block.match(itemPattern);
                                if (match) {
                                  console.log(`[PurchaseOrderView] Found potential item at index ${i}:`, block);
                                  itemIndices.push(i);
                                }
                              }
                            }
                            
                            console.log(`[PurchaseOrderView] Found ${itemIndices.length} potential item indices:`, itemIndices);
                          
                          // If we found potential item indices, extract them
                          if (itemIndices.length > 0) {
                            lineItems = itemIndices.map((idx, itemIdx) => {
                              const block = textBlocks[idx];
                              // Try full pattern first: quantity description price total
                              let match = block.match(/^(\d+(?:\.\d+)?)\s+(.+?)\s+(\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)$/);
                              
                              if (match) {
                                const quantity = parseFloat(match[1]) || 0;
                                const unitPrice = parseFloat(match[3]) || 0;
                                const lineTotal = parseFloat(match[4]) || 0;
                                // Calculate tax if line_total is different from quantity * unit_price
                                const baseTotal = quantity * unitPrice;
                                const taxAmount = lineTotal - baseTotal;
                                const taxRate = baseTotal > 0 ? (taxAmount / baseTotal) * 100 : 0;
                                
                                const item = {
                                  description: match[2].trim(),
                                  item_name: match[2].trim(),
                                  quantity: quantity,
                                  quantity_ordered: quantity,
                                  unit_cost: unitPrice,
                                  rate: unitPrice,
                                  unit_price: unitPrice,
                                  line_total: lineTotal,
                                  tax_rate: taxRate,
                                  tax_amount: taxAmount,
                                };
                                console.log(`[PurchaseOrderView] Extracted item ${itemIdx + 1}:`, item);
                                return item;
                              }
                              
                              // Try split format: quantity in one block, description in next, price in next
                              if (idx + 2 < textBlocks.length) {
                                const quantityBlock = textBlocks[idx];
                                const descBlock = textBlocks[idx + 1];
                                const priceBlock = textBlocks[idx + 2];
                                
                                if (typeof quantityBlock === 'string' && typeof descBlock === 'string' && typeof priceBlock === 'string') {
                                  const qtyMatch = quantityBlock.match(/^(\d+(?:\.\d+)?)/);
                                  const priceMatch = priceBlock.match(/(\d+(?:\.\d+)?)/);
                                  
                                  if (qtyMatch && priceMatch) {
                                    const quantity = parseFloat(qtyMatch[1]) || 0;
                                    const unitPrice = parseFloat(priceMatch[1]) || 0;
                                    const lineTotal = quantity * unitPrice; // Assume no tax for split format
                                    
                                    const item = {
                                      description: descBlock.trim(),
                                      item_name: descBlock.trim(),
                                      quantity: quantity,
                                      quantity_ordered: quantity,
                                      unit_cost: unitPrice,
                                      rate: unitPrice,
                                      unit_price: unitPrice,
                                      line_total: lineTotal,
                                      tax_rate: 0,
                                      tax_amount: 0,
                                    };
                                    console.log(`[PurchaseOrderView] Extracted item ${itemIdx + 1} (split format):`, item);
                                    return item;
                                  }
                                }
                              }
                              
                              return null;
                            }).filter(item => item !== null);
                            
                            if (lineItems.length > 0) {
                              console.log(`[PurchaseOrderView] Successfully extracted ${lineItems.length} line items from text_blocks:`, lineItems);
                            } else {
                              console.warn(`[PurchaseOrderView] Found ${itemIndices.length} potential items but couldn't parse them`);
                            }
                          } else {
                            console.log(`[PurchaseOrderView] No item patterns found in text_blocks`);
                          }
                        }
                      }
                        
                        // If still no items, try parsing from raw_text
                        if (lineItems.length === 0 && rawText) {
                          // Look for table-like patterns in raw_text
                          // Pattern: quantity, description, unit price, total
                          const lines = rawText.split('\n');
                          const itemLines = [];
                          
                          for (const line of lines) {
                            // Skip header lines and summary lines
                            if (/item|description|quantity|rate|amount|total|subtotal|discount|grand/i.test(line) && 
                                !/^\d/.test(line.trim())) {
                              continue;
                            }
                            
                            // Try to match item pattern: number, text, number, number
                            const itemMatch = line.match(/^(\d+(?:\.\d+)?)\s+(.+?)\s+(\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)/);
                            if (itemMatch) {
                              const quantity = parseFloat(itemMatch[1]) || 0;
                              const unitPrice = parseFloat(itemMatch[3]) || 0;
                              const lineTotal = parseFloat(itemMatch[4]) || 0;
                              // Calculate tax if line_total is different from quantity * unit_price
                              const baseTotal = quantity * unitPrice;
                              const taxAmount = lineTotal - baseTotal;
                              const taxRate = baseTotal > 0 ? (taxAmount / baseTotal) * 100 : 0;
                              
                              itemLines.push({
                                description: itemMatch[2].trim(),
                                item_name: itemMatch[2].trim(),
                                quantity: quantity,
                                quantity_ordered: quantity,
                                unit_cost: unitPrice,
                                rate: unitPrice,
                                unit_price: unitPrice,
                                line_total: lineTotal,
                                tax_rate: taxRate,
                                tax_amount: taxAmount,
                              });
                            }
                          }
                          
                          if (itemLines.length > 0) {
                            lineItems = itemLines;
                            console.log(`[PurchaseOrderView] Extracted ${lineItems.length} line items from raw_text`);
                          }
                        }
                      }
                      
                      // Calculate subtotal from line items if not already set
                      if (subtotal === 0 && lineItems.length > 0) {
                        subtotal = lineItems.reduce((sum, item) => {
                          return sum + (parseFloat(item.line_total) || parseFloat(item.amount) || 0);
                        }, 0);
                        console.log(`[PurchaseOrderView] Calculated subtotal from line items: ${subtotal}`);
                      }
                      
                      console.log(`[PurchaseOrderView] Final lineItems before return (${lineItems.length} items):`, lineItems);
                      
                      return {
                        po_id: fdocId,
                        po_number: poNumber,
                        po_date: poDate,
                        vendor_name: vendorName,
                        site_id: docSiteId,
                        site_name: siteName,
                        expected_delivery_date: expectedDeliveryDate,
                        payment_date: paymentDate,
                        payment_mode: paymentMode,
                        billing_address: billingAddress,
                        lines: lineItems,
                        subtotal: subtotal,
                        discount_amount: discountAmount,
                        freight_total: freightTotal,
                        total_amount: totalAmount,
                        status: 'Pending',
                        version_no: resolveFdocVersion(fullDoc, doc),
                        versionNo: resolveFdocVersion(fullDoc, doc),
                        // Store FDOC reference
                        fdoc_id: fdocId,
                        fdoc_document: fullDoc,
                      };
                    }
                    return null;
                  } catch (err) {
                    const isFileMissing = err.status === 410 || err.fileMissing === true;
                    if (isFileMissing) {
                      const docDate = doc.uploaded_at ? new Date(doc.uploaded_at).toISOString().split('T')[0] : null;
                      if (docDate) {
                        if (startDate && docDate < startDate) return null;
                        if (endDate && docDate > endDate) return null;
                      }
                      const missingId = doc?.fdoc_id || doc?.id || '';
                      // Auto-cancel missing-file FDOCs and hide them from the UI (no reupload/recreate flow).
                      await autoCancelMissingFdoc(missingId, 'file missing, auto-deleted from Purchase Orders list');
                      return null;
                    }
                    console.warn(`PurchaseOrderView: Failed to process FDOC document ${doc?.fdoc_id || doc?.id}:`, err.message);
                    return null;
                  }
                })
              );
              
              batchResults.forEach((result) => {
                if (result.status === 'fulfilled' && result.value) {
                  fdocPOs.push(result.value);
                }
              });
            }
            
            // Transform FDOC purchase orders to component format
            const sitesList = externalSites && externalSites.length > 0 ? externalSites : sites;
            const transformed = fdocPOs.map(po => {
              console.log(`[PurchaseOrderView] Transforming PO ${po.po_number}, items count:`, po.lines?.length || 0, po.lines);
              const transformedPo = transformPurchaseOrder(po);
              console.log(`[PurchaseOrderView] Transformed PO ${po.po_number}, items count:`, transformedPo.items?.length || 0, transformedPo.items);
              // Preserve vendor_name if it was set from FDOC (transformPurchaseOrder might have lost it)
              if (po.vendor_name && po.vendor_name !== 'Unknown Vendor') {
                transformedPo.vendor = po.vendor_name;
              }
              // If siteName is still N/A, try to look it up from sites list
              if ((transformedPo.siteName === 'N/A' || !transformedPo.siteName) && transformedPo.siteId && sitesList.length > 0) {
                const site = sitesList.find(s => resolveSiteValue(s) === transformedPo.siteId);
                if (site) {
                  transformedPo.siteName = site.site_name || site.name || 'N/A';
                }
              }
              // Store FDOC reference and fileMissing
              transformedPo.fdoc_id = po.fdoc_id;
              transformedPo.fdoc_document = po.fdoc_document;
              transformedPo.fileMissing = po.fileMissing || false;
              const v = resolveFdocVersion(po, po.fdoc_document);
              transformedPo.version_no = v;
              transformedPo.versionNo = v;
              console.log(`[PurchaseOrderView] Transformed PO ${po.po_number} vendor:`, transformedPo.vendor);
              return transformedPo;
            });

            setPurchaseOrders(transformed);
          } catch (fdocErr) {
            console.error('PurchaseOrderView: Error fetching purchase orders from FDOC:', fdocErr);
            setError(fdocErr.message || 'Failed to fetch purchase orders from FDOC');
            setPurchaseOrders([]);
          } finally {
            setIsLoading(false);
          }
          return;
        }

        // Regular purchase order API fetch
        console.log('PurchaseOrderView: Fetching purchase orders with:', {
          siteId,
          selectedMonth,
          startDate,
          endDate,
          siteName: selectedSite.site_name || selectedSite.name
        });

        const params = {
          site_id: siteId, // Pass site_id to API for server-side filtering
          start_date: startDate,
          end_date: endDate,
          limit: 100, // Fetch up to 100 purchase orders
        };

        // Add PO number filter if provided
        if (poNumberFilter.trim()) {
          params.po_number_contains = poNumberFilter.trim();
        }

        const result = await purchaseOrderApi.getPurchaseOrders(params);

        if (result.success) {
          // Get purchase orders from response - handle various response structures
          // The API wrapper transforms responseData.orders to result.purchase_orders
          let apiPurchaseOrders = null;
          
          // Check result.purchase_orders first (API wrapper transforms orders to purchase_orders)
          if (Array.isArray(result.purchase_orders)) {
            apiPurchaseOrders = result.purchase_orders;
          }
          // Check result.orders (fallback if API wrapper didn't transform)
          else if (Array.isArray(result.orders)) {
            apiPurchaseOrders = result.orders;
          }
          // Check result.data.orders
          else if (result.data && Array.isArray(result.data.orders)) {
            apiPurchaseOrders = result.data.orders;
          }
          // Check result.data
          else if (Array.isArray(result.data)) {
            apiPurchaseOrders = result.data;
          }
          // Check if result.data is an object with nested arrays
          else if (result.data && Array.isArray(result.data.purchase_orders)) {
            apiPurchaseOrders = result.data.purchase_orders;
          }
          else if (result.data && Array.isArray(result.data.items)) {
            apiPurchaseOrders = result.data.items;
          }
          else if (result.data && Array.isArray(result.data.data)) {
            apiPurchaseOrders = result.data.data;
          }
          // Check if the entire response is an array
          else if (Array.isArray(result)) {
            apiPurchaseOrders = result;
          }
          // Default to empty array
          else {
            console.warn('Purchase Order API: Unexpected response structure:', result);
            console.warn('Purchase Order API: Available keys:', Object.keys(result));
            apiPurchaseOrders = [];
          }
          
          console.log('Purchase Order API: Extracted purchase orders:', apiPurchaseOrders);
          
          // Ensure we have an array before mapping
          if (!Array.isArray(apiPurchaseOrders)) {
            console.error('Purchase Order API: apiPurchaseOrders is not an array:', typeof apiPurchaseOrders, apiPurchaseOrders);
            apiPurchaseOrders = [];
          }
          
          // Transform API data to component format
          const transformed = apiPurchaseOrders.map(po => {
            const transformedPo = transformPurchaseOrder(po);
            // If siteName is not in API response, look it up from sites list
            if (!transformedPo.siteName && transformedPo.siteId) {
              const site = sites.find(s => 
                (s.site_id || s.id) === transformedPo.siteId
              );
              if (site) {
                transformedPo.siteName = site.site_name || site.name || 'Unknown Site';
              }
            }
            return transformedPo;
          });
          
          const filtered = transformed;

          console.log('PurchaseOrderView: Filtered purchase orders:', {
            total: filtered.length,
            siteId,
            selectedMonth,
            dateRange: `${startDate} to ${endDate}`
          });

          if (filtered.length === 0 && siteId && selectedMonth) {
            console.warn('PurchaseOrderView: No purchase orders found for:', {
              siteId,
              selectedMonth,
              dateRange: `${startDate} to ${endDate}`,
              note: 'This might indicate a backend filtering issue if a PO was just created',
              suggestion: 'Try clicking the Refresh button or wait a few seconds and refresh again'
            });
            
            // If we just created a PO and got no results, suggest retrying
            if (location.state?.justCreated) {
              console.warn('PurchaseOrderView: PO was just created but not found. This could be:');
              console.warn('1. Backend eventual consistency delay - try refreshing in a few seconds');
              console.warn('2. Backend not storing site_id correctly during creation');
              console.warn('3. Backend not filtering by site_id correctly during fetch');
            }
          }

          setPurchaseOrders(filtered);
        } else {
          setError(result.message || 'Failed to fetch purchase orders');
          setPurchaseOrders([]);
        }
      } catch (err) {
        console.error('Error fetching purchase orders:', err);
        setError(err.message || 'Failed to fetch purchase orders');
        setPurchaseOrders([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPurchaseOrders();
  }, [selectedSite, selectedMonth, refreshKey, sites, fetchFromFdoc, poNumberFilter, esignedOnly]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }
    document.body.style.overflow = selectedCard ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [selectedCard]);

  // Fetch FDOC document when card is clicked (for OO purchase orders)
  useEffect(() => {
    if (!selectedCard) {
      setDownloadUrl(null);
      setDownloadUrlExpiresIn(null);
      setDocumentContentType(null);
      return;
    }

    console.log('PurchaseOrderView: Card selected:', {
      poNumber: selectedCard.poNumber,
      fdoc_id: selectedCard.fdoc_id,
      fetchFromFdoc,
      hasFdocId: !!selectedCard.fdoc_id,
    });

    // Check if this is an FDOC document (OO purchase order)
    if (selectedCard.fdoc_id || fetchFromFdoc) {
      if (selectedCard.fileMissing) {
        setIsLoadingDetails(false);
        return;
      }
      setIsLoadingDetails(true);
      const fetchFdocDocument = async () => {
        try {
          const fdocId = selectedCard.fdoc_id;
          if (!fdocId) {
            console.warn('PurchaseOrderView: No fdoc_id found on selected card. Card data:', selectedCard);
            console.warn('PurchaseOrderView: fetchFromFdoc is:', fetchFromFdoc);
            setIsLoadingDetails(false);
            return;
          }

          console.log('PurchaseOrderView: Fetching FDOC document with ID:', fdocId);

          console.log('PurchaseOrderView: Fetching FDOC document:', fdocId);
          const fullDoc = await fdocApi.getDocument(fdocId);
          console.log('FDOC Document fetched:', fullDoc);

          // Try to find a direct download URL first
          const downloadUrlFromDoc = fullDoc?.download_url || fullDoc?.file_url || fullDoc?.s3_url || fullDoc?.presigned_url || fullDoc?.view_url;
          if (downloadUrlFromDoc && (downloadUrlFromDoc.startsWith('http://') || downloadUrlFromDoc.startsWith('https://'))) {
            setDownloadUrl(downloadUrlFromDoc);
            setDocumentContentType(fullDoc?.content_type || fullDoc?.file_content_type || 'application/pdf');
          } else {
            // Get S3 key from document - try keys in priority order
            // Build list of available keys in priority order
            const availableKeys = [
              fullDoc?.s3_key_permanent,
              fullDoc?.s3_key,
              fullDoc?.s3_key_temp,
              fullDoc?.object_key
            ].filter(key => key && key.trim() !== '');
            
            console.log('PurchaseOrderView: Available S3 keys:', {
              s3_key_permanent: fullDoc?.s3_key_permanent,
              s3_key: fullDoc?.s3_key,
              s3_key_temp: fullDoc?.s3_key_temp,
              object_key: fullDoc?.object_key,
              availableKeys
            });
            
            if (availableKeys.length === 0) {
              console.warn('PurchaseOrderView: No S3 key or download URL found in FDOC document');
              return;
            }
            
            // Try each key in order until one works
            const bucket = fullDoc?.bucket || 'innoworx-fdoc-permanent';
            let presignedUrl = null;
            
            for (const s3Key of availableKeys) {
              try {
                // Check if it's already a URL
                if (s3Key.startsWith('http://') || s3Key.startsWith('https://')) {
                  console.log('PurchaseOrderView: Using direct URL:', s3Key);
                  setDownloadUrl(s3Key);
                  setDocumentContentType(fullDoc?.content_type || fullDoc?.file_content_type || 'application/pdf');
                  return;
                }
                
                // Convert S3 key to S3 path format
                const s3Path = `s3://${bucket}/${s3Key}`;
                console.log('PurchaseOrderView: Trying S3 path:', s3Path);
                
                // Get presigned URL from S3 path
                presignedUrl = await userService.getS3PresignedUrl(s3Path);
                if (presignedUrl) {
                  // Check if URL has wrong region (this will cause 400/403 errors)
                  if (presignedUrl.includes('ap-south-1')) {
                    console.error(
                      '❌ CRITICAL: Presigned URL region mismatch.\n' +
                      'The S3 bucket (innoworx-fdoc-permanent) is in us-east-1, but the presigned URL is generated with ap-south-1.\n' +
                      'This will cause 400/403 errors. Backend fix required: The /s3-presigned endpoint must detect the bucket region and generate URLs with us-east-1.'
                    );
                  }
                  console.log('PurchaseOrderView: Successfully got presigned URL for key:', s3Key);
                  setDownloadUrl(presignedUrl);
                  setDocumentContentType(fullDoc?.content_type || fullDoc?.file_content_type || fullDoc?.file_type ? `application/${fullDoc.file_type}` : 'application/pdf');
                  return; // Success, exit loop
                }
              } catch (error) {
                console.warn(`PurchaseOrderView: Failed to get presigned URL for key "${s3Key}":`, error);
                // Continue to next key
                continue;
              }
            }
            
            // If we get here, all keys failed
            console.error('PurchaseOrderView: All S3 keys failed to generate presigned URLs');
          }
          
          // Do NOT set purchase order details for FDOC documents to hide the preview
        } catch (err) {
          const isFileMissing = err.status === 410 || err.fileMissing === true;
          if (isFileMissing && selectedCard) {
            const fdocId = selectedCard.fdoc_id || selectedCard.id;
            await autoCancelMissingFdoc(fdocId, 'file missing, auto-deleted when opening Purchase Order');
            setPurchaseOrders((prev) => prev.filter((p) => (p.fdoc_id || p.id) !== fdocId));
            setSelectedCard(null);
            setDownloadUrl(null);
            setDownloadUrlExpiresIn(null);
            setDocumentContentType(null);
          } else {
            const errorMessage = err.message?.toLowerCase();
            if (err.status === 500 || errorMessage?.includes('500') || errorMessage?.includes('internal server error') || errorMessage?.includes('server error')) {
              console.error(`PurchaseOrderView: Document ${selectedCard.fdoc_id} is not accessible (server error). Silently skipping.`);
            } else {
              console.error('Error fetching FDOC document:', err);
            }
          }
        } finally {
          setIsLoadingDetails(false);
        }
      };

      fetchFdocDocument();
    } else {
      // For non-FDOC purchase orders, reset document states
      setDownloadUrl(null);
      setDownloadUrlExpiresIn(null);
      setDocumentContentType(null);
    }
  }, [selectedCard?.fdoc_id, selectedCard, fetchFromFdoc]);

  // Filter purchase orders by selected site, then by PO number. Include file-missing docs with no site so they still appear.
  const filteredPurchaseOrders = useMemo(() => {
    if (!selectedSite) return purchaseOrders;
    const siteId = resolveSiteValue(selectedSite);
    const normalizedSelectedSiteId = siteId != null ? String(siteId) : '';
    let list = purchaseOrders;
    if (siteId) {
      list = purchaseOrders.filter((po) => {
        const poSiteIdRaw = po.siteId ?? po.site_id ?? '';
        const normalizedPoSiteId = poSiteIdRaw != null ? String(poSiteIdRaw) : '';
        const matchesSite = normalizedPoSiteId === normalizedSelectedSiteId;
        return matchesSite;
      });
    }
    if (!poNumberFilter.trim()) {
      return list;
    }
    const filterLower = poNumberFilter.toLowerCase().trim();
    return list.filter(po => {
      const poNumber = po.poNumber || '';
      return poNumber.toLowerCase().includes(filterLower);
    });
  }, [purchaseOrders, selectedSite, poNumberFilter]);

  const formatCurrency = (amount = 0) =>
    Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(amount);

  const handleCancelFdoc = async (fdocId) => {
    if (!fdocId || cancellingFdocId) return;
    setCancellingFdocId(fdocId);
    try {
      await fdocApi.cancel(fdocId, { reason: 'file missing, deleted from UI' });
      setPurchaseOrders((prev) => prev.filter((p) => (p.fdoc_id || p.id) !== fdocId));
      if ((selectedCard?.fdoc_id || selectedCard?.id) === fdocId) {
        setSelectedCard(null);
        setDownloadUrl(null);
        setDownloadUrlExpiresIn(null);
        setDocumentContentType(null);
      }
    } catch (err) {
      console.error('PurchaseOrderView: Cancel document failed:', err);
      const msg = err?.message || 'Failed to delete document';
      setError(msg);
    } finally {
      setCancellingFdocId(null);
    }
  };

  const handleDownload = (po) => {
    if (!po) return;

    try {
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      let yPosition = margin;
      const lineHeight = 7;
      const sectionSpacing = 5;

      const centerText = (text, fontSize = 12, isBold = false) => {
        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', isBold ? 'bold' : 'normal');
        const textWidth = pdf.getTextWidth(text);
        const xPosition = (pageWidth - textWidth) / 2;
        pdf.text(text, xPosition, yPosition);
        yPosition += lineHeight;
      };

      const addText = (text, fontSize = 10, isBold = false) => {
        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', isBold ? 'bold' : 'normal');
        pdf.text(text, margin, yPosition);
        yPosition += lineHeight;
      };

      const addTwoColumn = (leftText, rightText, fontSize = 10, leftBold = false, rightBold = false) => {
        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', leftBold ? 'bold' : 'normal');
        pdf.text(leftText, margin, yPosition);
        
        pdf.setFont('helvetica', rightBold ? 'bold' : 'normal');
        const rightTextWidth = pdf.getTextWidth(rightText);
        pdf.text(rightText, pageWidth - margin - rightTextWidth, yPosition);
        yPosition += lineHeight;
      };

      const drawLine = () => {
        pdf.line(margin, yPosition, pageWidth - margin, yPosition);
        yPosition += sectionSpacing;
      };

      // Header
      centerText('PURCHASE ORDER', 18, true);
      yPosition += 3;
      if (po.siteName) {
        centerText(po.siteName, 12, true);
        yPosition += 2;
      }
      drawLine();
      yPosition += sectionSpacing;

      // PO Information
      addText('PO INFORMATION', 12, true);
      yPosition += 2;
      addTwoColumn('PO Number:', po.poNumber || 'N/A', 10);
      addTwoColumn('PO Date:', po.poDate || 'N/A', 10);
      if (po.expectedDeliveryDate) {
        addTwoColumn('Expected Delivery:', po.expectedDeliveryDate, 10);
      }
      if (po.paymentDate) {
        addTwoColumn('Payment Date:', po.paymentDate, 10);
      }
      if (po.paymentMode) {
        addTwoColumn('Payment Mode:', po.paymentMode, 10);
      }
      if (po.siteName) {
        addTwoColumn('Site:', po.siteName, 10);
      }
      addTwoColumn('Status:', po.status || 'N/A', 10);
      yPosition += sectionSpacing;
      drawLine();
      yPosition += sectionSpacing;

      // Vendor Information
      if (po.vendor || po.billingAddress) {
        addText('VENDOR INFORMATION', 12, true);
        yPosition += 2;
        if (po.vendor) {
          addTwoColumn('Vendor:', po.vendor, 10);
        }
        if (po.billingAddress) {
          const addr = po.billingAddress;
          if (addr.firstName || addr.lastName) {
            addTwoColumn('Name:', `${addr.firstName || ''} ${addr.lastName || ''}`.trim(), 10);
          }
          if (addr.email) {
            addTwoColumn('Email:', addr.email, 10);
          }
          if (addr.address) {
            addTwoColumn('Address:', addr.address, 10);
          }
          if (addr.city || addr.state) {
            addTwoColumn('City/State:', `${addr.city || ''}, ${addr.state || ''}`.trim(), 10);
          }
          if (addr.mobileNumber) {
            addTwoColumn('Mobile:', addr.mobileNumber, 10);
          }
          if (addr.gstNumber) {
            addTwoColumn('GST:', addr.gstNumber, 10);
          }
        }
        yPosition += sectionSpacing;
        drawLine();
        yPosition += sectionSpacing;
      }

      // Items Header
      addText('ITEMS', 12, true);
      yPosition += 3;

      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'bold');
      pdf.text('S.No', margin, yPosition);
      pdf.text('Name', margin + 20, yPosition);
      pdf.text('HSN', margin + 80, yPosition);
      pdf.text('Units', margin + 100, yPosition);
      pdf.text('Rate', margin + 120, yPosition);
      pdf.text('Tax%', margin + 140, yPosition);
      pdf.text('Amount', pageWidth - margin - 30, yPosition, { align: 'right' });
      yPosition += lineHeight;
      drawLine();
      yPosition += 2;

      // Items List
      if (po.items && po.items.length > 0) {
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        po.items.forEach((item, index) => {
          if (yPosition > pageHeight - 40) {
            pdf.addPage();
            yPosition = margin;
          }

          const itemName = item.name || `Item ${index + 1}`;
          const itemLines = pdf.splitTextToSize(itemName, 50);
          const itemHeight = itemLines.length * 5;

          pdf.text((index + 1).toString(), margin, yPosition);
          pdf.text(itemLines, margin + 20, yPosition);
          pdf.text(item.hsnCode || 'N/A', margin + 80, yPosition);
          pdf.text(item.units || 'N/A', margin + 100, yPosition);
          pdf.text(formatCurrency(item.rate || 0), margin + 120, yPosition);
          pdf.text(`${item.tax || 0}%`, margin + 140, yPosition);
          pdf.text(formatCurrency(item.amount || 0), pageWidth - margin, yPosition, { align: 'right' });
          yPosition += Math.max(itemHeight, lineHeight) + 2;
        });
      } else {
        pdf.setFontSize(9);
        pdf.text('No items found', margin, yPosition);
        yPosition += lineHeight;
      }

      yPosition += sectionSpacing;
      drawLine();
      yPosition += sectionSpacing;

      if (yPosition > pageHeight - 60) {
        pdf.addPage();
        yPosition = margin;
      }

      // Summary
      addText('SUMMARY', 12, true);
      yPosition += 2;
      addTwoColumn('Subtotal:', formatCurrency(po.subtotal || 0), 10);
      if (po.discount > 0) {
        addTwoColumn('Discount:', formatCurrency(po.discount || 0), 10);
      }
      yPosition += sectionSpacing;
      drawLine();
      yPosition += sectionSpacing;
      addTwoColumn('GRAND TOTAL:', formatCurrency(po.grandTotal || 0), 14, true, true);
      yPosition += sectionSpacing;
      drawLine();
      yPosition += sectionSpacing * 2;

      centerText('Thank you!', 10);

      pdf.save(`purchase-order-${po.poNumber || 'unknown'}-${po.poDate || 'unknown'}.pdf`);
    } catch (error) {
      console.error('Failed to generate purchase order PDF', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  // Animation variants
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.2 } },
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'Approved':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'Pending':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  return (
    <div className="w-full min-h-screen">
      <div className="mx-auto h-auto">
        <motion.div
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          className="space-y-8"
        >
          <div className="rounded-3xl">
            {/* PO Number Filter - Always visible, Site and Month Selection - Only show if not hidden */}
            <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between mb-6">
              <div className="min-w-[200px] flex-1">
                <label htmlFor="po-number-filter" className="inline-flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700">
                  <Search className="w-4 h-4" />
                  Filter by PO Number
                </label>
                <input
                  id="po-number-filter"
                  type="text"
                  placeholder="Enter PO Number or PO ID..."
                  value={poNumberFilter}
                  onChange={(event) => setPoNumberFilter(event.target.value)}
                  className="w-full min-h-[48px] rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
                />
              </div>

              {!hideSelectors && (
                <>
                <div className="min-w-[200px]">
                  <label htmlFor="purchase-order-site" className="inline-flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700">
                    <MapPin className="w-4 h-4" />
                    Select Site
                  </label>
                  <select
                    id="purchase-order-site"
                    value={selectedSite ? resolveSiteValue(selectedSite) : ''}
                    onChange={(e) => {
                      const siteId = e.target.value;
                      const site = sites.find(s => resolveSiteValue(s) === siteId);
                      setSelectedSite(site || null);
                    }}
                    className="w-full min-h-[48px] rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
                    required
                    disabled={isSitesLoading}
                  >
                    <option value="">
                      {isSitesLoading ? 'Loading sites...' : 'Select a site'}
                    </option>
                    {sites.map((site) => (
                      <option key={resolveSiteValue(site)} value={resolveSiteValue(site)}>
                        {site.site_name || site.name || 'Unknown Site'}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="min-w-[200px]">
                  <label htmlFor="purchase-order-month" className="inline-flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700">
                    <Calendar className="w-4 h-4" />
                    Select Month
                  </label>
                  <input
                    id="purchase-order-month"
                    type="month"
                    value={selectedMonth}
                    onChange={(event) => setSelectedMonth(event.target.value)}
                    className="w-full min-h-[48px] rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
                  />
                </div>
                </>
              )}
            </div>

            {/* Purchase Orders List */}
            {isLoading && (
              <div className="text-center py-12">
                <p className="text-gray-500">Loading purchase orders...</p>
              </div>
            )}
            
            {error && !isLoading && (
              <div className="text-center py-12">
                <p className="text-red-500">Error: {error}</p>
              </div>
            )}

            {!hideSelectors && !isLoading && !error && filteredPurchaseOrders.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500 mb-4">No purchase orders found for the selected site and month.</p>
                <p className="text-sm text-gray-400 mb-4">
                  Site: {selectedSite?.site_name || selectedSite?.name || 'N/A'} | 
                  Month: {selectedMonth}
                </p>
                <button
                  onClick={() => {
                    console.log('PurchaseOrderView: Retry fetch after no results');
                    setRefreshKey(prev => prev + 1);
                  }}
                  className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-100"
                >
                  Retry
                </button>
              </div>
            )}

            {!isLoading && !error && filteredPurchaseOrders.length > 0 && (
              <div className="overflow-x-auto rounded-3xl border border-gray-200 bg-white shadow-sm">
                <table className="min-w-full border-collapse text-sm md:text-base">
                  <thead>
                    <tr className="bg-gray-50 text-left text-gray-600">
                      <th className="px-4 py-3 font-semibold">S.No</th>
                      <th className="px-4 py-3 font-semibold min-w-[160px]">PO Number</th>
                      <th className="px-4 py-3 font-semibold min-w-[140px]">Date</th>
                      <th className="px-4 py-3 font-semibold min-w-[220px]">Vendor</th>
                      <th className="px-4 py-3 font-semibold min-w-[180px]">Site</th>
                      <th className="px-4 py-3 font-semibold min-w-[100px]">Version</th>
                      <th className="px-4 py-3 font-semibold min-w-[140px]">Grand Total</th>
                      <th className="px-4 py-3 font-semibold min-w-[140px]">Status</th>
                      <th className="px-4 py-3 font-semibold min-w-[220px]">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPurchaseOrders.map((po, index) => {
                      const id = po.fdoc_id || po.id;
                      const rowVersion = resolveFdocVersion(po, po.fdoc_document);
                      return (
                        <tr
                          key={id || `po-row-${index}`}
                          className="border-t border-gray-100 hover:bg-gray-50/60 cursor-pointer"
                          onClick={() => setSelectedCard(po)}
                        >
                          <td className="px-4 py-4 align-top text-gray-800">{index + 1}</td>
                          <td className="px-4 py-4 align-top text-gray-800">{po.poNumber || '—'}</td>
                          <td className="px-4 py-4 align-top text-gray-700">{po.poDate || '—'}</td>
                          <td className="px-4 py-4 align-top text-gray-700">
                            <div className="max-w-[320px] truncate" title={po.vendor || ''}>
                              {po.vendor || '—'}
                            </div>
                          </td>
                          <td className="px-4 py-4 align-top text-gray-700">
                            <div className="max-w-[260px] truncate" title={po.siteName || ''}>
                              {po.siteName || '—'}
                            </div>
                          </td>
                          <td className="px-4 py-4 align-top text-gray-800">
                            {rowVersion != null && rowVersion !== '' ? `v${rowVersion}` : '—'}
                          </td>
                          <td className="px-4 py-4 align-top text-gray-800">
                            ₹{(po.grandTotal ?? 0).toFixed(2)}
                          </td>
                          <td className="px-4 py-4 align-top">
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold border ${getStatusColor(po.status)}`}>
                              {po.status || '—'}
                            </span>
                          </td>
                          <td className="px-4 py-4 align-top">
                            <div className="flex flex-wrap gap-2">
                              {!po.fileMissing && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDownload(po);
                                  }}
                                  className="inline-flex items-center gap-2 rounded-lg bg-blue-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-800"
                                  title="Download"
                                >
                                  <Download className="h-4 w-4 text-white stroke-white" strokeWidth={2} />
                                  Download
                                </button>
                              )}
                              {po.fileMissing && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCancelFdoc(id);
                                  }}
                                  disabled={cancellingFdocId === id}
                                  className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50"
                                >
                                  {cancellingFdocId === id ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Trash2 className="h-4 w-4" />
                                  )}
                                  {cancellingFdocId === id ? 'Deleting…' : 'Delete entry'}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {!isLoading && !error && filteredPurchaseOrders.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500">No purchase orders found matching the selected criteria.</p>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Detail Modal */}
      {selectedCard &&
        createPortal(
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[9999] flex flex-col bg-black/70 min-h-screen w-full"
            >
            <div className="flex-shrink-0 px-4 pt-6 pb-4">
              <div className="mx-auto flex w-full max-w-5xl flex-wrap items-center justify-between gap-3">
                <div className="text-sm font-semibold uppercase tracking-widest text-white/80">
                  {(selectedCard?.fdoc_id || fetchFromFdoc) ? 'Document' : 'Purchase Order Details'}
                </div>
                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedCard(null);
                      setDownloadUrl(null);
                      setDownloadUrlExpiresIn(null);
                      setDocumentContentType(null);
                    }}
                    className="rounded-full border border-white/40 bg-white/15 px-5 py-2 text-sm font-semibold text-white transition hover:bg-white/25"
                  >
                    Close
                  </button>
                  {!(selectedCard?.fdoc_id || fetchFromFdoc) && (
                    <button
                      type="button"
                      onClick={() => handleDownload(selectedCard)}
                      className="rounded-full bg-emerald-500 px-6 py-2 text-sm font-semibold text-white shadow-lg transition hover:bg-emerald-600"
                    >
                      <span className="inline-flex items-center gap-2">
                        <Download className="w-4 h-4 text-white stroke-white" strokeWidth={2} />
                        Download PDF
                      </span>
                    </button>
                  )}
                  {(selectedCard?.fdoc_id || fetchFromFdoc) && selectedCard?.fileMissing && (
                    <button
                      type="button"
                      onClick={() => handleCancelFdoc(selectedCard.fdoc_id || selectedCard.id)}
                      disabled={!!cancellingFdocId}
                      className="rounded-full bg-red-600 px-6 py-2 text-sm font-semibold text-white shadow-lg transition hover:bg-red-700 disabled:opacity-50"
                    >
                      <span className="inline-flex items-center gap-2">
                        {cancellingFdocId ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                        {cancellingFdocId ? 'Deleting...' : 'Delete entry'}
                      </span>
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-4 pb-6">
              <motion.div
                variants={modalVariants}
                initial="hidden"
                animate="visible"
                exit="hidden"
                className="mx-auto w-full max-w-5xl rounded-[32px] bg-white p-6 text-gray-800 shadow-2xl sm:p-8"
              >
                {isLoadingDetails ? (
                  <div className="text-center py-12">
                    <p className="text-gray-500">Loading...</p>
                  </div>
                ) : (
                  <>
                    {/* For OO (FDOC) purchase orders: Only show PDF document, no preview */}
                    {(selectedCard?.fdoc_id || fetchFromFdoc) ? (
                      <>
                        {selectedCard?.fileMissing ? (
                          <div className="py-8 px-4 rounded-xl bg-amber-50 border border-amber-200" role="alert">
                            <p className="text-amber-800 font-semibold">Source file is no longer available in storage.</p>
                            <p className="text-amber-700 mt-2">Please re-upload the document.</p>
                            <button
                              type="button"
                              onClick={() => handleCancelFdoc(selectedCard.fdoc_id || selectedCard.id)}
                              disabled={!!cancellingFdocId}
                              className="mt-4 inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                            >
                              {cancellingFdocId ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                              {cancellingFdocId ? 'Deleting...' : 'Delete entry'}
                            </button>
                          </div>
                        ) : downloadUrl ? (
                          <div className="w-full h-full min-h-[80vh]">
                            {documentContentType && documentContentType.startsWith('image/') ? (
                              // Display images using img tag
                              <div className="flex justify-center items-center w-full h-full min-h-[80vh] p-4">
                                <img
                                  src={downloadUrl}
                                  alt="Purchase Order Document"
                                  className="max-w-full max-h-full object-contain rounded-lg"
                                />
                              </div>
                            ) : (
                              // Display PDFs using iframe - full screen
                              <div className="w-full h-full min-h-[80vh]">
                                <iframe
                                  src={downloadUrl}
                                  title="Purchase Order Document"
                                  className="w-full h-full min-h-[80vh] border-0"
                                  onError={() => {
                                    console.error('Failed to load PDF document. URL may be invalid or expired.');
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="text-center py-12">
                            <div className="max-w-md mx-auto">
                              <p className="text-gray-500 mb-2">No document available or failed to load.</p>
                              {downloadUrl && (
                                <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                                  <p className="text-sm font-semibold text-yellow-800 mb-1">
                                    Document URL Error
                                  </p>
                                  <p className="text-xs text-yellow-700">
                                    The document URL could not be loaded. This is likely due to a backend configuration issue where presigned URLs are generated with the wrong AWS region (ap-south-1 instead of us-east-1).
                                  </p>
                                  <p className="text-xs text-yellow-700 mt-2">
                                    Please contact support or check backend logs for region mismatch errors.
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        {/* For non-OO purchase orders: Show purchase order details, no document viewer */}
                        <div className="flex items-start justify-between mb-6">
                          <div>
                            <h2 className="text-2xl font-semibold text-gray-900">{selectedCard.poNumber}</h2>
                            <p className="text-gray-500 mt-1">{selectedCard.vendor}</p>
                          </div>
                        </div>

                        <div className="grid gap-6 md:grid-cols-2 mb-6">
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">PO Information</h3>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-500">PO Date:</span>
                                <span className="font-medium text-gray-700">{selectedCard.poDate}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Expected Delivery:</span>
                                <span className="font-medium text-gray-700">{selectedCard.expectedDeliveryDate}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Payment Date:</span>
                                <span className="font-medium text-gray-700">{selectedCard.paymentDate}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Payment Mode:</span>
                                <span className="font-medium text-gray-700">{selectedCard.paymentMode}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Site:</span>
                                <span className="font-medium text-gray-700">{selectedCard.siteName}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Status:</span>
                                <span className={`px-2 py-1 rounded-full text-xs font-semibold border ${getStatusColor(selectedCard.status)}`}>
                                  {selectedCard.status}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Billing Address</h3>
                            <div className="space-y-1 text-sm text-gray-700">
                              <p>{selectedCard.billingAddress.firstName} {selectedCard.billingAddress.lastName}</p>
                              <p>{selectedCard.billingAddress.email}</p>
                              <p>{selectedCard.billingAddress.address}</p>
                              <p>{selectedCard.billingAddress.city}, {selectedCard.billingAddress.state}</p>
                              <p>{selectedCard.billingAddress.country} - {selectedCard.billingAddress.pinCode}</p>
                              <p className="mt-2">Mobile: {selectedCard.billingAddress.mobileNumber}</p>
                              <p>PAN: {selectedCard.billingAddress.panNumber}</p>
                              <p>GST: {selectedCard.billingAddress.gstNumber}</p>
                            </div>
                          </div>
                        </div>

                        <div className="mb-6">
                          <h3 className="text-lg font-semibold text-gray-900 mb-4">Items</h3>
                          <div className="overflow-x-auto rounded-2xl border border-gray-200">
                            <table className="min-w-full border-collapse text-sm">
                              <thead>
                                <tr className="bg-gray-50 text-left text-gray-600">
                                  <th className="px-4 py-3 font-semibold">S.No</th>
                                  <th className="px-4 py-3 font-semibold">Name</th>
                                  <th className="px-4 py-3 font-semibold">HSN Code</th>
                                  <th className="px-4 py-3 font-semibold">Units</th>
                                  <th className="px-4 py-3 font-semibold">Rate</th>
                                  <th className="px-4 py-3 font-semibold">Tax (%)</th>
                                  <th className="px-4 py-3 font-semibold text-right">Amount</th>
                                </tr>
                              </thead>
                              <tbody>
                                {selectedCard.items.map((item, index) => (
                                  <tr key={index} className="border-t border-gray-100">
                                    <td className="px-4 py-3">{index + 1}</td>
                                    <td className="px-4 py-3">{item.name}</td>
                                    <td className="px-4 py-3">{item.hsnCode}</td>
                                    <td className="px-4 py-3">{item.units}</td>
                                    <td className="px-4 py-3">₹{item.rate.toFixed(2)}</td>
                                    <td className="px-4 py-3">{item.tax}%</td>
                                    <td className="px-4 py-3 text-right">₹{item.amount.toFixed(2)}</td>
                                  </tr>
                                ))}
                                <tr className="border-t border-gray-200 bg-gray-50 text-gray-700">
                                  <td colSpan={6} className="px-4 py-3 font-semibold">Subtotal</td>
                                  <td className="px-4 py-3 text-right font-semibold">₹{selectedCard.subtotal.toFixed(2)}</td>
                                </tr>
                                <tr className="border-t border-gray-100 bg-white text-gray-700">
                                  <td colSpan={6} className="px-4 py-3 font-semibold">Discount</td>
                                  <td className="px-4 py-3 text-right font-semibold">₹{selectedCard.discount.toFixed(2)}</td>
                                </tr>
                                <tr className="border-t border-gray-200 bg-gray-100 text-gray-900 font-semibold">
                                  <td colSpan={6} className="px-4 py-3">Grand Total</td>
                                  <td className="px-4 py-3 text-right">₹{selectedCard.grandTotal.toFixed(2)}</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </>
                    )}
                  </>
                )}
              </motion.div>
            </div>
          </motion.div>
          </AnimatePresence>,
          document.body
        )}

    </div>
  );
};

export default PurchaseOrderView;

